import {
  LineChart,
  Line,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';

interface MiniTrendProps {
  data: { value: number }[];
  color: string;
  areaColor: string;
}

function MiniTrend({ data, color, areaColor }: MiniTrendProps) {
  return (
    <div className="w-full h-14">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id={`grad-${areaColor}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.25} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#grad-${areaColor})`}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

interface StatsOverviewProps {
  stats: {
    totalColaboradores: number;
    totalSolicitacoesEPI: number;
    solicitacoesPendentes: number;
    incidentesRegistrados: number;
    denunciasRecebidas: number;
    solicitacoesAprovadas: number;
    solicitacoesNegadas: number;
  };
  monthlyData: { name: string; epi: number; incidentes: number; denuncias: number }[];
}

export default function StatsOverview({ stats, monthlyData }: StatsOverviewProps) {
  const epiTrend = monthlyData.map((m) => ({ value: m.epi }));
  const incTrend = monthlyData.map((m) => ({ value: m.incidentes }));
  const denTrend = monthlyData.map((m) => ({ value: m.denuncias }));

  const items = [
    {
      label: 'Colaboradores Ativos',
      value: stats.totalColaboradores,
      icon: 'ri-group-line',
      color: '#5B3DF5',
      bg: 'bg-portal-purple-50',
      trend: epiTrend,
      trendKey: 'epi',
    },
    {
      label: 'EPIs Solicitados',
      value: stats.totalSolicitacoesEPI,
      icon: 'ri-tools-line',
      color: '#7B61FF',
      bg: 'bg-portal-purple-50',
      trend: epiTrend,
      trendKey: 'epi2',
    },
    {
      label: 'Incidentes Registrados',
      value: stats.incidentesRegistrados,
      icon: 'ri-alarm-warning-line',
      color: '#F59E0B',
      bg: 'bg-amber-50',
      trend: incTrend,
      trendKey: 'inc',
    },
    {
      label: 'Denúncias Recebidas',
      value: stats.denunciasRecebidas,
      icon: 'ri-shield-user-line',
      color: '#EF4444',
      bg: 'bg-red-50',
      trend: denTrend,
      trendKey: 'den',
    },
    {
      label: 'Taxa de Aprovação',
      value:
        stats.totalSolicitacoesEPI > 0
          ?Math.round((stats.solicitacoesAprovadas / stats.totalSolicitacoesEPI) * 100)
          : 0,
      suffix: '%',
      icon: 'ri-check-double-line',
      color: '#10B981',
      bg: 'bg-emerald-50',
      trend: monthlyData.map((_, i) => ({
        value: stats.totalSolicitacoesEPI > 0
          ?Math.round(((stats.solicitacoesAprovadas / stats.totalSolicitacoesEPI) * 100) * (1 + i * 0.02))
          : 0,
      })),
      trendKey: 'apr',
    },
    {
      label: 'Taxa de Recusa',
      value:
        stats.totalSolicitacoesEPI > 0
          ?Math.round((stats.solicitacoesNegadas / stats.totalSolicitacoesEPI) * 100)
          : 0,
      suffix: '%',
      icon: 'ri-close-circle-line',
      color: '#EF4444',
      bg: 'bg-red-50',
      trend: monthlyData.map((_, i) => ({
        value: stats.totalSolicitacoesEPI > 0
          ?Math.round(((stats.solicitacoesNegadas / stats.totalSolicitacoesEPI) * 100) * (1 + i * 0.01))
          : 0,
      })),
      trendKey: 'rej',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      {items.map((item) => (
        <div
          key={item.trendKey}
          className="bg-white rounded-xl border border-portal-gray-200 p-5 hover:border-portal-purple-200 transition-colors duration-200"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-portal-gray-500 whitespace-nowrap">{item.label}</p>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.bg}`}>
              <i className={`${item.icon} text-sm`} style={{ color: item.color }}></i>
            </div>
          </div>
          <p className="text-2xl font-bold text-portal-gray-900">
            {item.value}
            {item.suffix && <span className="text-sm font-medium text-portal-gray-500 ml-1">{item.suffix}</span>}
          </p>
          <div className="mt-2">
            <MiniTrend data={item.trend} color={item.color} areaColor={item.trendKey} />
          </div>
        </div>
      ))}
    </div>
  );
}